import { createSelector, Selector } from 'reselect';

import { getIsDistributable } from 'core/utils/date.helpers';

import {
  INITIALIZE_DETAIL,
  SET_WIDGET_STATUS,
  WIDGET_GLOBAL_VIEW_CHALLENGES,
  WIDGET_POINTS_DETAILS,
} from 'actions/widget.actions';
import {
  ActionType,
  ChallengesType,
  ErrorTypes,
  LoadingStatusType,
  OperationDetailsType,
  OperationType,
} from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';

export type PointDetailType = {
  data: [OperationType, [OperationDetailsType], number] | null;
  error: ErrorTypes | null;
};

export type WidgetChallengesType = {
  data: ChallengesType[] | null;
  error: ErrorTypes | null;
};
export interface IWidgetState {
  status: {
    [widgetId: string]: LoadingStatusType;
  };
  challenges: {
    [widgetId: string]: WidgetChallengesType;
  };
  pointDetail: {
    [widgetId: string]: PointDetailType;
  };
}

type WidgetSelector<T> = Selector<{ widget: IWidgetState }, T>;

const detailInitialState = {
  data: null,
  error: null,
};

export const initialState: IWidgetState = {
  status: {},
  challenges: {},
  pointDetail: {},
};

export const widgetReducerSelector = (state: {
  widget: IWidgetState;
}): IWidgetState => state.widget;

export const widgetStatusSelector = (
  widgetId: string | number,
): WidgetSelector<LoadingStatusType> =>
  createSelector(
    widgetReducerSelector,
    (widget: IWidgetState) => widget.status[widgetId],
  );

export const widgetPointsDetailsSelector = (
  widgetId: string | number,
): WidgetSelector<PointDetailType> =>
  createSelector(
    widgetReducerSelector,
    (widget: IWidgetState) => widget.pointDetail[widgetId] || {},
  );

export const widgetGlobalViewChallengesSelector = (
  widgetId: string | number,
): WidgetSelector<WidgetChallengesType> =>
  createSelector(widgetReducerSelector, (widget: IWidgetState) => {
    const challenges = widget.challenges[widgetId];
    if (!challenges) {
      return { data: null, error: null };
    }

    const distributableChallengeData =
      challenges.data &&
      challenges.data.filter(
        ({ startDiffusionDate, endDiffusionDate }: ChallengesType) =>
          getIsDistributable(startDiffusionDate, endDiffusionDate),
      );
    return { ...challenges, data: distributableChallengeData };
  });

export const widgetReducer = (
  state: IWidgetState = initialState,
  action: ActionType,
): IWidgetState => {
  const { payload } = action;

  switch (action.type) {
    case INITIALIZE_DETAIL: {
      const { detailType }: { detailType: 'challenges' | 'pointDetail' } =
        payload;
      if (state[detailType][payload.widgetId]) {
        return state;
      }
      return {
        ...state,
        [detailType]: {
          ...state[detailType],
          [payload.widgetId]: { ...detailInitialState },
        },
      };
    }
    case WIDGET_POINTS_DETAILS[SUCCESS]:
      return {
        ...state,
        pointDetail: {
          ...state.pointDetail,
          [payload.widgetId]: {
            ...state.pointDetail[payload.widgetId],
            data: payload.data,
            error: null,
          },
        },
      };
    case WIDGET_POINTS_DETAILS[FAILURE]:
      return {
        ...state,
        pointDetail: {
          ...state.pointDetail,
          [payload.widgetId]: {
            ...state.pointDetail[payload.widgetId],
            data: null,
            error: payload.error,
          },
        },
      };
    case WIDGET_GLOBAL_VIEW_CHALLENGES[SUCCESS]:
      return {
        ...state,
        challenges: {
          ...state.challenges,
          [payload.widgetId]: {
            ...state.challenges[payload.widgetId],
            data: payload.challenges,
            error: null,
          },
        },
      };
    case WIDGET_GLOBAL_VIEW_CHALLENGES[FAILURE]:
      return {
        ...state,
        challenges: {
          ...state.challenges,
          [payload.widgetId]: {
            ...state.challenges[payload.widgetId],
            data: null,
            error: payload.error,
          },
        },
      };
    case SET_WIDGET_STATUS:
      return {
        ...state,
        status: {
          ...state.status,
          [payload.widgetId]: payload.status,
        },
      };
    default:
      return state;
  }
};

export default widgetReducer;
